import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, cn, cssVars, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { downloadFile } from "../shared/download";
import { useBuildFormatters } from "../shared/format";
import type { EvalRun } from "../shared/types";
import { METRICS, PASS_THRESHOLD, configView, deltaTone, formatDelta, metricDelta, metricValue, totalCases, triggerType, type Metric } from "./eval-model";

const PERCENT = 100;
const DELTA_TONE = { up: "text-success", down: "text-danger", same: "text-fg-subtle" } as const;

function MetricBar({ run, metric }: { run: EvalRun; metric: Metric }) {
  const { t } = useTranslation();
  const value = metricValue(run, metric);
  const delta = metricDelta(run, metric);
  const below = value !== null && value < PASS_THRESHOLD;
  return (
    <div data-testid={`metric-${metric}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-bold">{t(`build.evaluations.metrics.${metric}`)}</span>
        <span className="flex items-baseline gap-2">
          {delta === null ? null : <span className={cn("text-xs font-semibold", DELTA_TONE[deltaTone(delta)])}>{formatDelta(delta)}</span>}
          <span className="text-lg font-extrabold tabular-nums">{value === null ? t("build.evaluations.metrics.noScore") : value.toFixed(2)}</span>
        </span>
      </div>
      <div
        className="relative mt-1.5 h-2 rounded-pill bg-sunken"
        role="meter"
        aria-label={t(`build.evaluations.metrics.${metric}`)}
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={value ?? 0}
        aria-valuetext={value === null ? t("build.evaluations.metrics.noScore") : `${value.toFixed(2)} · ${t("build.evaluations.metrics.threshold", { value: PASS_THRESHOLD })}`}
      >
        <div className={cn("h-2 w-[var(--geo-width)] rounded-pill", below ? "bg-accent" : "bg-primary")} style={cssVars({ "--geo-width": `${String(Math.round((value ?? 0) * PERCENT))}%` })} />
        <span aria-hidden="true" className="absolute -top-1 left-[var(--geo-left)] h-4 w-0.5 bg-fg-subtle" style={cssVars({ "--geo-left": `${String(PASS_THRESHOLD * PERCENT)}%` })} />
      </div>
      <p className="mt-1.5 text-xs text-fg-subtle">{t(`build.evaluations.metrics.${metric}Body`)}</p>
    </div>
  );
}

function PassRate({ run, previous }: { run: EvalRun; previous: EvalRun | undefined }) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const total = totalCases(run);
  const pct = (n: number) => `${String(total ? Math.round((n / total) * PERCENT) : 0)}%`;
  const change = previous ? run.pass_count - previous.pass_count : null;
  const config = configView(run);
  const rows = [
    [t("build.evaluations.latest.model"), config.model],
    [t("build.evaluations.latest.chunk"), config.chunkSize === null ? null : t("build.evaluations.latest.chunkValue", { value: config.chunkSize })],
    [t("build.evaluations.latest.reranker"), config.reranker || config.topK !== null ? t("build.evaluations.latest.rerankerValue", { reranker: config.reranker ?? "—", k: config.topK ?? "—" }) : null],
    [t("build.evaluations.latest.judge"), run.judge_model],
    [t("build.evaluations.latest.duration"), run.duration_ms === null ? null : fmt.duration(run.duration_ms)],
  ] as const;
  return (
    <div className="rounded-lg bg-surface-2 p-4 lg:col-span-1">
      <div className="stat-label">{t("build.evaluations.latest.passRate")}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-4xl font-extrabold tracking-tight tabular-nums">{run.pass_count}</span>
        <span className="text-lg font-bold text-fg-subtle">/ {total}</span>
      </div>
      {change === null || !previous ? null : (
        <div className={cn("mt-1 flex items-center gap-1 text-xs font-semibold", change >= 0 ? "text-success" : "text-danger")}>
          <Icon name={change >= 0 ? "trending-up" : "trending-down"} className="size-3.5" />
          {t("build.evaluations.latest.vsPrevious", { value: `${change >= 0 ? "+" : "−"}${String(Math.abs(change))}`, number: previous.number })}
        </div>
      )}
      <div className="mt-3 flex h-2 overflow-hidden rounded-pill bg-sunken" aria-hidden="true">
        <div className="h-2 w-[var(--geo-pass)] bg-mint-500" style={cssVars({ "--geo-pass": pct(run.pass_count) })} />
        <div className="h-2 w-[var(--geo-partial)] bg-signal-500" style={cssVars({ "--geo-partial": pct(run.partial_count) })} />
        <div className="h-2 w-[var(--geo-fail)] bg-rose-500" style={cssVars({ "--geo-fail": pct(run.fail_count) })} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-semibold text-fg-muted">
        <span className="flex items-center gap-1"><span className="size-2 rounded-xs bg-mint-500" />{t("build.evaluations.latest.pass", { count: run.pass_count })}</span>
        <span className="flex items-center gap-1"><span className="size-2 rounded-xs bg-signal-500" />{t("build.evaluations.latest.partial", { count: run.partial_count })}</span>
        <span className="flex items-center gap-1"><span className="size-2 rounded-xs bg-rose-500" />{t("build.evaluations.latest.fail", { count: run.fail_count })}</span>
      </div>
      <div className="divider my-4" />
      <dl className="space-y-2 text-xs">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3"><dt className="text-fg-subtle">{label}</dt><dd className="truncate text-right font-semibold">{value ?? "—"}</dd></div>
        ))}
      </dl>
    </div>
  );
}

interface LatestRunCardProps {
  run: EvalRun;
  previous: EvalRun | undefined;
  setName: string;
  onCompare: (a: EvalRun, b: EvalRun) => void;
  onExport: (runId: string) => Promise<string>;
}

/** "Latest run #n" card: pass-rate column + six metric bars with the 0.85 threshold marker. */
export function LatestRunCard({ run, previous, setName, onCompare, onExport }: LatestRunCardProps) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const message = useErrorMessage();
  const [exporting, setExporting] = useState(false);
  const trigger = triggerType(run);
  const status = run.threshold_passed === null || run.threshold_passed === undefined
    ? { tone: "brand" as const, label: t("build.evaluations.latest.running") }
    : run.threshold_passed ? { tone: "success" as const, label: t("build.evaluations.latest.passed") } : { tone: "danger" as const, label: t("build.evaluations.latest.failed") };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const file = `eval-run-${String(run.number)}.csv`;
      downloadFile(file, await onExport(run.id), "text/csv;charset=utf-8");
      toast.success(t("build.evaluations.latest.exported", { file }));
    } catch (error) {
      toast.error(message(error));
    } finally {
      setExporting(false);
    }
  };
  return (
    <section className="card" aria-labelledby="latest-run-title" data-testid="latest-run">
      <div className="card-header flex-wrap gap-3">
        <div>
          <h2 id="latest-run-title" className="card-title flex flex-wrap items-center gap-2">
            {t("build.evaluations.latest.title")}
            <span className="font-mono text-xs font-medium text-fg-subtle">{t("build.evaluations.latest.number", { number: run.number })}</span>
            <Badge tone={status.tone} dot>{status.label}</Badge>
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("build.evaluations.latest.meta", { set: setName, date: fmt.dateTime(run.finished_at ?? run.created_at), trigger: t(`build.evaluations.history.triggers.${trigger}`, { defaultValue: trigger }) })}</p>
        </div>
        <div className="flex items-center gap-2">
          {previous ? (
            <Button variant="ghost" size="sm" onClick={() => { onCompare(previous, run); }}>
              <Icon name="git-branch" />
              {t("build.evaluations.latest.compare", { number: previous.number })}
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" loading={exporting} onClick={() => void exportCsv()}>
            <Icon name="download" />
            {t("build.evaluations.latest.export")}
          </Button>
        </div>
      </div>
      <div className="card-body grid grid-cols-1 gap-6 lg:grid-cols-4">
        <PassRate run={run} previous={previous} />
        <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:col-span-3">
          {METRICS.map((metric) => <MetricBar key={metric} run={run} metric={metric} />)}
          <p className="flex items-center gap-2 text-[11px] text-fg-subtle sm:col-span-2">
            <span aria-hidden="true" className="inline-block h-3 w-0.5 bg-fg-subtle" />
            {t("build.evaluations.latest.marker", { threshold: PASS_THRESHOLD })}
          </p>
        </div>
      </div>
    </section>
  );
}
